import { apiClient, assertValidParam } from '@v2/lib';
import type {
  MissionControlPlugin,
  PluginVersion,
  DefaultMissionControlVersion,
  MissionControlVersion,
  PluginPimsDependency,
  PluginDefaultStatus,
} from '@v2/types';

export interface ManagePluginPayload {
  id: string;
  name: string;
  defaultVersionId?: string | null;
  defaultStartupTimeout: number;
  pimsDependencies: PluginPimsDependency[];
  isInactive: boolean;
  defaultStatus: PluginDefaultStatus;
  releaseTime?: string | null;
}

export interface GetPluginsParams {
  pimsFilter?: string;
  includeInactivePlugins?: boolean;
}

/**
 * Mission Control Service
 *
 * Domain: Mission Control plugin management, versions, and default version
 *
 * Note: GET endpoints use lowercase "missionControl", mutation endpoints use
 * PascalCase "MissionControl". This mirrors the current backend routing.
 *
 * Queries:
 *   - getPlugins(params?)           -> GET missionControl/plugins
 *   - getPluginDetails(pluginId)    -> GET missionControl/plugins/:id
 *   - getPluginVersions(pluginId)   -> GET missionControl/plugins/versions/:id
 *   - getDefaultVersion()           -> GET MissionControl/missionControlVersion/default
 *   - getAllVersions()              -> GET MissionControl/missionControlVersion/all
 *
 * Mutations:
 *   - setDefaultVersion(version)    -> PATCH MissionControl/missionControlVersion/default
 *   - managePlugin(payload)         -> PUT MissionControl/plugin
 */

export const missionControlService = {
  getPlugins: async (params?: GetPluginsParams): Promise<MissionControlPlugin[]> => {
    const searchParams = new URLSearchParams();
    if (params?.pimsFilter?.trim()) {
      searchParams.set('pimsFilter', params.pimsFilter.trim());
    }
    if (params?.includeInactivePlugins) {
      searchParams.set('includeInactivePlugins', 'true');
    }
    const query = searchParams.toString();
    const url = query ? `missionControl/plugins?${query}` : 'missionControl/plugins';
    const response = await apiClient().get<MissionControlPlugin[]>(url);
    return Array.isArray(response.data) ? response.data : [];
  },

  getPluginDetails: async (pluginId: string): Promise<MissionControlPlugin | null> => {
    const safeId = assertValidParam(pluginId, 'pluginId');
    const response = await apiClient().get<MissionControlPlugin>(
      `missionControl/plugins/${safeId}`
    );
    return response.data ?? null;
  },

  getPluginVersions: async (pluginId: string): Promise<PluginVersion[]> => {
    const safeId = assertValidParam(pluginId, 'pluginId');
    const response = await apiClient().get<PluginVersion[]>(
      `missionControl/plugins/versions/${safeId}`
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  getDefaultVersion: async (): Promise<DefaultMissionControlVersion | null> => {
    const response = await apiClient().get<DefaultMissionControlVersion>(
      'MissionControl/missionControlVersion/default'
    );
    return response.data ?? null;
  },

  getAllVersions: async (): Promise<MissionControlVersion[]> => {
    const response = await apiClient().get<
      MissionControlVersion[] | { data: MissionControlVersion[] }
    >('MissionControl/missionControlVersion/all');
    const raw = response.data;
    if (Array.isArray(raw)) return raw;
    if (raw && Array.isArray((raw as { data: MissionControlVersion[] }).data)) {
      return (raw as { data: MissionControlVersion[] }).data;
    }
    return [];
  },

  setDefaultVersion: async (versionString: string): Promise<void> => {
    await apiClient().patch(
      `MissionControl/missionControlVersion/default?versionString=${encodeURIComponent(versionString)}`
    );
  },

  managePlugin: async (payload: ManagePluginPayload): Promise<MissionControlPlugin | null> => {
    const response = await apiClient().put<MissionControlPlugin>(
      'MissionControl/plugin',
      payload
    );
    return response.data ?? null;
  },
};
